/**
 * QuestionBanksPage — /question-banks
 * Admin + Teacher: manage Question Banks (Savollar bazasi).
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  listQuestionBanks, createQuestionBank, updateQuestionBank, deleteQuestionBank,
  listSubjects,
} from '@/api/resources'
import { Button, PageHeader, EmptyState, Spinner, FIELD_CLS, FIELD_AUTO_CLS, Badge, SearchInput, TableFooter } from '@/components/ui/primitives'
import { Pencil, Trash2, ArrowRight, Plus, Upload, X, Database } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'
import { fetchAllPages } from '@/utils/fetchAll'

const PAGE_SIZE = 20

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800 dark:text-white">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function BankForm({ initial = {}, subjects, onSave, onCancel, loading }) {
  const { t } = useTranslation()
  const [name,        setName]        = useState(initial.name || '')
  const [description, setDescription] = useState(initial.description || '')
  const [subjectId,   setSubjectId]   = useState(initial.subject || '')

  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ name, description, subject: subjectId || null }) }} className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('banks.name', 'Baza nomi')} *</label>
        <input value={name} onChange={e => setName(e.target.value)} required className={FIELD_CLS} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.description', 'Tavsif')}</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className={FIELD_CLS} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('subjects.title', 'Fan')}</label>
        <select value={subjectId} onChange={e => setSubjectId(e.target.value)} className={FIELD_CLS}>
          <option value="">— Fan tanlanmagan —</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" type="button" onClick={onCancel}>{t('common.cancel', 'Bekor')}</Button>
        <Button variant="primary" type="submit" disabled={loading}>{loading ? t('common.saving', 'Saqlanmoqda...') : t('common.save', 'Saqlash')}</Button>
      </div>
    </form>
  )
}


export default function QuestionBanksPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [banks,    setBanks]    = useState([])
  const [loading,  setLoading]  = useState(true)
  const [subjects, setSubjects] = useState([])
  const [filter,   setFilter]   = useState('')
  const [search,   setSearch]   = useState('')
  const [page,     setPage]     = useState(1)
  const [modal,    setModal]    = useState(null)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState(null)
  const [confirm,  setConfirm]  = useState(null)

  const subjectsFetchedRef = useRef(false)
  useEffect(() => {
    if (subjectsFetchedRef.current) return
    subjectsFetchedRef.current = true
    fetchAllPages(listSubjects, { all: 1 }).then(setSubjects)
  }, [])

  useEffect(() => {
    setLoading(true)
    const params = {}
    if (filter) params.subject = filter
    if (search) params.search  = search
    fetchAllPages(listQuestionBanks, params)
      .then(setBanks)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [filter, search])

  useEffect(() => { setPage(1) }, [filter, search])

  async function handleSave(data) {
    setSaving(true); setError(null)
    try {
      if (modal === 'create') {
        const { data: created } = await createQuestionBank(data)
        setBanks(prev => [created, ...prev])
      } else {
        const { data: updated } = await updateQuestionBank(modal.id, data)
        setBanks(prev => prev.map(b => b.id === updated.id ? updated : b))
      }
      setModal(null)
    } catch (e) {
      setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message)
    } finally { setSaving(false) }
  }

  async function handleConfirmDelete() {
    try {
      await deleteQuestionBank(confirm)
      setBanks(prev => prev.filter(b => b.id !== confirm))
    } catch (e) { setError(e.response?.data?.detail || e.message) }
    finally { setConfirm(null) }
  }

  const totalPages = Math.ceil(banks.length / PAGE_SIZE)
  const paginated  = banks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('nav.questionBanks', 'Savollar bazasi')}
        actions={
          <>
            <Button variant="secondary" icon={<Upload size={15} />} onClick={() => navigate('/question-banks/import')}>
              {t('wordImport.import', 'Import')}
            </Button>
            <Button icon={<Plus size={15} />} onClick={() => setModal('create')}>
              {t('banks.add', 'Baza yaratish')}
            </Button>
          </>
        }
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
      )}

      <div className="flex items-center gap-3">
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('common.search', 'Qidirish...')}
        />
        <select value={filter} onChange={e => setFilter(e.target.value)} className={FIELD_AUTO_CLS}>
          <option value="">{t('subjects.all', 'Barcha fanlar')}</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="tbl-wrap">
        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : banks.length === 0 ? (
          <EmptyState icon={<Database size={48} />} title={t('banks.empty', 'Savollar bazasi topilmadi')} />
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr className="">
                  <th className="">Baza nomi</th>
                  <th className="">Fan</th>
                  <th className="">Savollar</th>
                  <th className="">Yaratuvchi</th>
                  <th className="">Amallar</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(bank => (
                  <tr key={bank.id} className="">
                    <td className="">
                      <p className="primary">{bank.name}</p>
                      {bank.description && <p className="secondary truncate max-w-xs">{bank.description}</p>}
                    </td>
                    <td className="">{bank.subject_name || <span className="secondary">—</span>}</td>
                    <td className="">
                      <Badge variant="green">{bank.question_count} ta</Badge>
                    </td>
                    <td className=""><span className="secondary">{bank.created_by?.full_name || bank.created_by?.username || '—'}</span></td>
                    <td className="tr">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => navigate(`/questions?bank=${bank.id}`)}
                          className="tbl-btn tbl-go"
                          title="Savollarga o'tish"
                        ><ArrowRight size={15} /></button>
                        <button onClick={() => setModal(bank)} className="tbl-btn tbl-edit" title="Tahrirlash"><Pencil size={15} /></button>
                        <button onClick={() => setConfirm(bank.id)} className="tbl-btn tbl-danger" title="O'chirish"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TableFooter page={page} totalPages={totalPages} totalCount={banks.length} onPageChange={setPage} />
          </>
        )}
      </div>

      {modal !== null && (
        <Modal
          title={modal === 'create' ? t('banks.add', 'Baza yaratish') : t('banks.edit', 'Bazani tahrirlash')}
          onClose={() => setModal(null)}
        >
          <BankForm
            initial={modal === 'create' ? {} : modal}
            subjects={subjects}
            onSave={handleSave}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        </Modal>
      )}

      {confirm !== null && (
        <ConfirmDialog
          title="O'chirishni tasdiqlang"
          message="Haqiqatan ham o'chirmoqchimisiz?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}
